import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
    { key: "all", label: "All", kinds: [] },
    { key: "birth", label: "Birth", kinds: ["birth", "farrowing_followup"] },
    { key: "vaccination", label: "Vaccination", kinds: ["vaccination"] },
    { key: "medicine", label: "Medicine", kinds: ["withdrawal", "treatment", "follow_up", "medical_schedule", "medical_schedule_completed"] },
    { key: "pregnancy", label: "Pregnancy", kinds: ["gestation_due_today", "gestation_due_soon", "gestation_overdue"] },
    { key: "low_stock", label: "Low Stock", kinds: ["low_stock"] },
    { key: "feed", label: "Feed", kinds: ["feed"] },
    { key: "due_task", label: "Due Tasks", kinds: ["due_task"] },
];

export default function NotificationsPage() {
    const [data, setData] = useState({ counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
    const [filter, setFilter] = useState("all");

    const load = async () => {
        try {
            const response = await api.get("/alerts");
            setData(response.data || { counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
        } catch (error) {
            toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to load notifications");
            setData({ counts: { total: 0, critical: 0, warning: 0, info: 0 }, alerts: [] });
        }
    };

    useEffect(() => {
        load();
    }, []);

    const activeGroup = useMemo(() => GROUPS.find((group) => group.key === filter) || GROUPS[0], [filter]);
    const filteredAlerts = useMemo(() => {
        const alerts = Array.isArray(data.alerts) ? data.alerts : [];
        if (!activeGroup || activeGroup.key === "all") {
            return alerts;
        }
        return alerts.filter((item) => activeGroup.kinds.includes(String(item.kind || "")));
    }, [activeGroup, data]);

    return (
        <div className="space-y-6" data-testid="notifications-page">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-primary" />
                <div>
                    <div className="tiny-label">{i18n.t("batch4.d0efe0b2c75c")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch4.753a22b2eb61")}</h1>
                </div>
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="notifications-refresh"><RefreshCw className="w-4 h-4 mr-1" />{i18n.t("batch4.56e3badc4e6c")}</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                {GROUPS.map((group) => {
                    const count = group.key === "all"
                        ? data.counts.total
                        : (data.alerts || []).filter((item) => group.kinds.includes(String(item.kind || ""))).length;
                    return (
                        <button
                            key={group.key}
                            className={`rounded-lg border px-3 py-2 text-left text-sm transition ${filter === group.key ? "ring-2 ring-primary bg-primary/5" : "bg-card"}`}
                            onClick={() => setFilter(group.key)}
                        >
                            <div className="font-medium">{group.label}</div>
                            <div className="text-muted-foreground">{count}</div>
                        </button>
                    );
                })}
            </div>

            <div>
                <div className="tiny-label mb-3">{activeGroup.label}</div>
                <div className="space-y-3">
                    {filteredAlerts.map((r) => (
                        <Card key={r.id} className="p-5 flex justify-between items-center hover:-translate-y-0.5 transition-transform">
                            <div>
                                <div className="font-semibold text-lg">{r.title}</div>
                                <div className="text-sm text-muted-foreground">{String(r.kind || "").replace(/_/g, " ")} · {r.detail}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="capitalize">{r.severity || "info"}</Badge>
                                <div className="font-mono text-primary">{r.date || "—"}</div>
                            </div>
                        </Card>
                    ))}
                    {filteredAlerts.length === 0 && <Card className="p-8 text-center text-muted-foreground">{i18n.t("batch4.2afb94d0898f")}</Card>}
                </div>
            </div>
        </div>
    );
}
